'use client';

import { decorateHref, resolveEfTid } from '@pickleballinc/lib/everflow/url';
import { useEffect } from 'react';

interface EverflowLinkDecoratorProps {
	/** Registrable domains we own — build it once with `buildEverflowDomains` and pass a stable reference. */
	domains: string[];
}

/**
 * `pointerdown` runs before the context menu and before a middle-click, so "copy link
 * address" / "open in new tab" carry the id too; `click` covers keyboard activation;
 * `auxclick` covers the middle-click release.
 */
const INTERACTION_EVENTS = ['pointerdown', 'click', 'auxclick'] as const;

/**
 * Keeps the Everflow transaction id with the visitor when they leave for another one of
 * our domains. One delegated, capture-phase listener rewrites the pressed `<a href>` at
 * interaction time — URL first, then the `pb_ef_tid` cookie — so links rendered after
 * page load and hrefs set after hydration are covered, and nothing third-party is loaded.
 * Programmatic navigations (`router.push`, `window.location.href`) use `withEfTid` instead.
 */
export const EverflowLinkDecorator = ({ domains }: EverflowLinkDecoratorProps) => {
	useEffect(() => {
		if (domains.length === 0) return;

		const onInteraction = (event: Event) => {
			const { target } = event;
			if (!(target instanceof Element)) return;

			const anchor = target.closest<HTMLAnchorElement>('a[href]');
			if (!anchor) return;

			const tid = resolveEfTid(window.location.search, document.cookie);
			if (!tid) return;

			const decorated = decorateHref(anchor.href, tid, { currentHostname: window.location.hostname, domains });
			if (decorated) anchor.href = decorated;
		};

		INTERACTION_EVENTS.forEach((type) => document.addEventListener(type, onInteraction, true));

		return () => {
			INTERACTION_EVENTS.forEach((type) => document.removeEventListener(type, onInteraction, true));
		};
	}, [domains]);

	return null;
};
